import './UserFeedback.scss'

import { HDSButton } from '@here/hds-react-components/Button'
import { HDSInput } from '@here/hds-react-components/Input'
import { HDSTextarea } from '@here/hds-react-components/Textarea'
import classNames from 'classnames'
import CloseButton from 'components/App/components/CloseButton'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { TranslateWithLink } from 'components/App/components/TranslateWithLink/TranslateWithLink'
import { addNotificationToQueue } from 'components/Notifications'
import { useAppDispatch } from 'data/store/hooks'
import { Field, Form, FormikProvider, useFormik } from 'formik'
import { NpsAnalytics, NpsEvent } from 'lib/analytics'
import { getFaqLocalizedLink } from 'lib/faq'
import { i18n } from 'lib/i18n'
import React from 'react'

import { HereAccount } from '../data/reducer'

interface UserFeedbackProps {
  onClick: () => void
  user?: HereAccount
}

export const NPSOptions = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

export const UserFeedback = React.forwardRef(function UserFeedback(
  props: UserFeedbackProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { onClick } = props
  const dispatch = useAppDispatch()

  const formik = useFormik({
    initialValues: {
      email: props.user ? props.user.email : '',
      reason: '',
      score: '',
    },
    onSubmit: (values) => {
      formik.setSubmitting(true)

      const analytics = new NpsAnalytics()

      analytics
        .init()
        .then(() =>
          analytics
            .logAppEvent(NpsEvent.NpsFeedbackSubmitter, {
              allow_contact: Boolean(values.email),
              email: values.email,
              feedback: values.reason,
              score: values.score,
            })
            .then(() => {
              dispatch(
                addNotificationToQueue({
                  content: i18n('user-feedback.modal.description-success'),
                  title: i18n('user-feedback.modal.subtitle-success'),
                  variant: 'confirmation',
                })
              )
              formik.setSubmitting(false)
              onClick()
            })
            .catch(console.error)
        )
        .catch(console.error)
    },
  })

  const handleOnClick: (value: string) => void = (value: string) => {
    formik.setFieldValue('score', value).catch(console.error)
  }

  const showExtraContent = (): boolean => NPSOptions.includes(formik.values['score'])

  const { privacy } = getFaqLocalizedLink()

  return (
    <FormikProvider value={formik}>
      <Form>
        <div className="UserFeedback" data-testid="user-feedback" ref={ref}>
          <div className="UserFeedback__header" data-testid="user-feedback-title">
            {i18n('user-feedback.modal.title')}
            <CloseButton onClick={onClick} />
          </div>
          <ScrollableContainer>
            <React.Fragment>
              <div className="UserFeedback__content">
                <React.Fragment>
                  <div className="UserFeedback__subtitle" data-testid="user-feedback-subtitle">
                    {i18n('user-feedback.modal.subtitle')}
                  </div>
                  <div className="UserFeedback__question" data-testid="user-feedback-nps-question">
                    {i18n('user-feedback.modal.nps-question')}
                  </div>
                  <div role="group" aria-labelledby="my-radio-group" className="UserFeedback__NPS">
                    {NPSOptions.map((option) => (
                      <Field
                        key={option}
                        type="radio"
                        name="score"
                        value={option}
                        data-testid="user-feedback-options-input"
                      >
                        {() => (
                          <span
                            data-testid={`user-feedback-option-${option}`}
                            className={classNames('UserFeedback__option', {
                              'UserFeedback__option--disabled':
                                Boolean(formik.values['score']) &&
                                formik.values['score'] !== option,
                              'UserFeedback__option--selected': formik.values['score'] === option,
                            })}
                            onClick={() => handleOnClick(option)}
                          >
                            {option}
                          </span>
                        )}
                      </Field>
                    ))}
                  </div>
                  <div className="UserFeedback__legend" data-testid="user-feedback-legend">
                    <span>{i18n('user-feedback.modal.legend.bad')}</span>
                    <span>{i18n('user-feedback.modal.legend.good')}</span>
                  </div>
                </React.Fragment>
              </div>
              {showExtraContent() && (
                <div
                  className="UserFeedback__extra-content"
                  data-testid="user-feedback-extra-content"
                >
                  <Field
                    as={HDSTextarea}
                    name="reason"
                    rows={4}
                    className="UserFeedback__reason"
                    placeholder={i18n('user-feedback.modal.reason.placeholder')}
                    data-testid="user-feedback-reason-input"
                    label={
                      Number(formik.values['score']) > 8
                        ? i18n('user-feedback.modal.reason.grateful-label')
                        : i18n('user-feedback.modal.reason.label')
                    }
                  />
                  <Field
                    as={HDSInput}
                    name="email"
                    type="email"
                    data-testid="user-feedback-email-input"
                    placeholder={i18n('user-feedback.modal.email.placeholder')}
                    label={i18n('user-feedback.modal.email.label')}
                  />
                  <div className="UserFeedback__assistive" data-testid="user-feedback-assistive">
                    <TranslateWithLink
                      translationKey="user-feedback.modal.assistive"
                      url={privacy}
                    />
                  </div>
                </div>
              )}

              <div className="UserFeedback__bottom" data-testid="user-feedback-bottom">
                <HDSButton
                  data-testid="user-feedback-submit"
                  size="medium"
                  width="100%"
                  loading={formik.isSubmitting}
                  disabled={!NPSOptions.includes(formik.values['score'])}
                  onClick={() => {
                    if (!formik.isSubmitting) void formik.submitForm()
                  }}
                >
                  {i18n('user-feedback.modal.submit')}
                </HDSButton>
              </div>
            </React.Fragment>
          </ScrollableContainer>
        </div>
      </Form>
    </FormikProvider>
  )
})
